import React, { useEffect, useState } from "react";
import { ShieldCheck, Power, PowerOff, Building2, Users, Phone, Mic, Sparkles, Save } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function SuperAdmin() {
  const { t } = useI18n();
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [plat, setPlat] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = async () => {
    const [t1, t2, t3] = await Promise.all([api.get("/admin/tenants"), api.get("/admin/stats"), api.get("/admin/platform-settings")]);
    setTenants(t1.data); setStats(t2.data);
    setPlat(t3.data);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.put(`/admin/tenants/${id}/status`, { status });
    await load();
  };

  const savePlat = async () => {
    if (!plat) return;
    setSaving(true); setSaved(false);
    await api.put("/admin/platform-settings", {
      stt_enable_kenlm: !!plat.stt_enable_kenlm,
      stt_enable_openai_correction: !!plat.stt_enable_openai_correction,
      stt_openai_model: plat.stt_openai_model || "gpt-4o",
      stt_openai_prompt: plat.stt_openai_prompt || "",
    });
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 1200);
    await load();
  };

  return (
    <div className="space-y-6" data-testid="superadmin-page">
      <div>
        <div className="chip"><ShieldCheck className="h-3 w-3" /> {t("superadmin.title")}</div>
        <h1 className="mt-3 text-3xl lg:text-4xl font-extrabold text-white tracking-tight">{t("superadmin.title")}</h1>
        <p className="mt-1 text-slate-400 text-sm">{t("superadmin.sub")}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass rounded-2xl p-6"><div className="text-xs uppercase tracking-widest text-slate-400 font-bold flex items-center gap-2"><Building2 className="h-3.5 w-3.5"/>{t("superadmin.tenants")}</div><div className="mt-2 text-3xl font-extrabold text-white font-mono">{stats?.tenants ?? "—"}</div></div>
        <div className="glass rounded-2xl p-6"><div className="text-xs uppercase tracking-widest text-slate-400 font-bold">{t("superadmin.active")}</div><div className="mt-2 text-3xl font-extrabold text-emerald-400 font-mono">{stats?.active_tenants ?? "—"}</div></div>
        <div className="glass rounded-2xl p-6"><div className="text-xs uppercase tracking-widest text-slate-400 font-bold flex items-center gap-2"><Users className="h-3.5 w-3.5"/>{t("superadmin.total_users")}</div><div className="mt-2 text-3xl font-extrabold text-white font-mono">{stats?.users ?? "—"}</div></div>
        <div className="glass rounded-2xl p-6"><div className="text-xs uppercase tracking-widest text-slate-400 font-bold flex items-center gap-2"><Phone className="h-3.5 w-3.5"/>{t("superadmin.total_calls")}</div><div className="mt-2 text-3xl font-extrabold text-magenta font-mono">{stats?.total_calls ?? "—"}</div></div>
      </div>

      <div className="glass rounded-2xl p-6" data-testid="platform-settings">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">{t("superadmin.platform_title")}</div>
            <div className="mt-1 text-white font-semibold">{t("superadmin.platform_sub")}</div>
          </div>
          <button onClick={savePlat} disabled={!plat || saving} className="btn-primary inline-flex items-center gap-2 text-xs" data-testid="platform-save">
            <Save className="h-3.5 w-3.5" /> {saved ? t("superadmin.saved") : t("superadmin.save")}
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white font-semibold">
                <Mic className="h-4 w-4 text-magenta" /> {t("superadmin.kenlm")}
              </div>
              <button
                onClick={() => setPlat((p) => ({ ...p, stt_enable_kenlm: !p.stt_enable_kenlm }))}
                className={`px-3 py-1 rounded-full text-xs font-bold border ${plat?.stt_enable_kenlm ? "bg-emerald-400/10 text-emerald-300 border-emerald-400/20" : "bg-rose-400/10 text-rose-300 border-rose-400/20"}`}
                data-testid="toggle-kenlm"
                disabled={!plat}
              >
                {plat?.stt_enable_kenlm ? t("superadmin.enabled") : t("superadmin.disabled")}
              </button>
            </div>
            <div className="mt-2 text-xs text-slate-400">{t("superadmin.kenlm_help")}</div>
          </div>

          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white font-semibold">
                <Sparkles className="h-4 w-4 text-magenta" /> {t("superadmin.openai_correct")}
              </div>
              <button
                onClick={() => setPlat((p) => ({ ...p, stt_enable_openai_correction: !p.stt_enable_openai_correction }))}
                className={`px-3 py-1 rounded-full text-xs font-bold border ${plat?.stt_enable_openai_correction ? "bg-emerald-400/10 text-emerald-300 border-emerald-400/20" : "bg-rose-400/10 text-rose-300 border-rose-400/20"}`}
                data-testid="toggle-openai-correct"
                disabled={!plat}
              >
                {plat?.stt_enable_openai_correction ? t("superadmin.enabled") : t("superadmin.disabled")}
              </button>
            </div>
            <div className="mt-2 text-xs text-slate-400">{t("superadmin.openai_help")}</div>

            <div className="mt-4 grid grid-cols-1 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{t("superadmin.openai_model")}</div>
                <input
                  value={plat?.stt_openai_model || ""}
                  onChange={(e) => setPlat((p) => ({ ...p, stt_openai_model: e.target.value }))}
                  className="input-dark mt-1.5"
                  placeholder="gpt-4o"
                  disabled={!plat}
                  data-testid="openai-model"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{t("superadmin.openai_prompt")}</div>
                <textarea
                  value={plat?.stt_openai_prompt || ""}
                  onChange={(e) => setPlat((p) => ({ ...p, stt_openai_prompt: e.target.value }))}
                  className="input-dark mt-1.5 min-h-[120px]"
                  disabled={!plat}
                  data-testid="openai-prompt"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              <tr>
                <th className="text-left px-5 py-3">{t("superadmin.company")}</th>
                <th className="text-left px-5 py-3">{t("superadmin.plan")}</th>
                <th className="text-left px-5 py-3">{t("superadmin.status")}</th>
                <th className="text-left px-5 py-3">{t("superadmin.users")}</th>
                <th className="text-left px-5 py-3">{t("superadmin.calls")}</th>
                <th className="text-left px-5 py-3">{t("superadmin.created")}</th>
                <th className="text-right px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tn) => (
                <tr key={tn.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`tenant-row-${tn.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-bold text-white">{tn.company_name}</div>
                    <div className="font-mono text-xs text-magenta">{tn.phone_number}</div>
                  </td>
                  <td className="px-5 py-3 capitalize text-slate-300">{tn.plan}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border ${tn.status === "active" ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/20" : "text-rose-400 bg-rose-400/10 border-rose-400/20"}`}>{tn.status}</span>
                  </td>
                  <td className="px-5 py-3 font-mono text-slate-300">{tn.users_count}</td>
                  <td className="px-5 py-3 font-mono text-slate-300">{tn.calls_count}</td>
                  <td className="px-5 py-3 font-mono text-slate-500 text-xs">{(tn.created_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-3 text-right">
                    {tn.status === "active" ? (
                      <button onClick={() => setStatus(tn.id, "suspended")} className="btn-ghost inline-flex items-center gap-2 text-xs" data-testid={`tenant-suspend-${tn.id}`}><PowerOff className="h-3.5 w-3.5"/>{t("superadmin.action_suspend")}</button>
                    ) : (
                      <button onClick={() => setStatus(tn.id, "active")} className="btn-primary inline-flex items-center gap-2 text-xs" data-testid={`tenant-activate-${tn.id}`}><Power className="h-3.5 w-3.5"/>{t("superadmin.action_activate")}</button>
                    )}
                  </td>
                </tr>
              ))}
              {tenants.length === 0 && <tr><td colSpan={7} className="text-center px-5 py-10 text-slate-500">—</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
